import React, { useState } from 'react';

import { CheckCircle, CreditCard } from 'lucide-react';

import { API_BASE_URL } from '../../config/config';
import {
  InvoiceClient,
  InvoicePaymentSummaryDTO,
} from '../../services/apiClient';
import axiosInstance from '../../services/axiosInstance';

const invoiceClient = new InvoiceClient(API_BASE_URL, axiosInstance);

interface PaymentSummaryProps {
  summary: InvoicePaymentSummaryDTO;
  title?: string;
  // The two standalone pages (Pay.tsx, PaymentRequired.tsx) sit on AuthLayout's light background,
  // but MemberSetupDrawer embeds this inline on its dark charcoal panel - this swaps the palette
  // rather than forcing every caller onto one look.
  dark?: boolean;
}

const money = (amount: number, currency: string): string =>
  `${currency} ${amount.toFixed(2)}`;

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  summary,
  title = 'Membership Payment',
  dark = false,
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePayNow = async (): Promise<void> => {
    setError(null);
    setIsRedirecting(true);
    try {
      const response = await invoiceClient.createCheckoutSession(
        summary.invoiceId ?? '',
      );
      if (response?.success && response.data) {
        window.location.href = response.data;
        return;
      }
      setError(
        response?.message ?? 'Could not start checkout. Please try again.',
      );
    } catch (err: unknown) {
      const apiMessage = (err as { result?: { message?: string } })?.result
        ?.message;
      setError(
        typeof apiMessage === 'string' && apiMessage.trim().length > 0
          ? apiMessage
          : 'Could not start checkout. Please try again.',
      );
    } finally {
      setIsRedirecting(false);
    }
  };

  if (summary.isPaid) {
    return (
      <div
        className={
          dark
            ? 'rounded-xl border border-green-500/30 bg-green-500/10 p-6 text-center'
            : 'rounded-xl border border-green-200 bg-green-50 p-6 text-center'
        }
      >
        <CheckCircle
          className={`mx-auto mb-3 h-10 w-10 ${dark ? 'text-green-400' : 'text-green-600'}`}
        />
        <p
          className={`text-lg font-semibold ${dark ? 'text-green-300' : 'text-green-800'}`}
        >
          This invoice has already been paid
        </p>
        {summary.invoiceNumber && (
          <p
            className={`mt-1 text-sm ${dark ? 'text-green-400/80' : 'text-green-700'}`}
          >
            Invoice {summary.invoiceNumber}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        dark
          ? 'rounded-xl border border-primary/25 bg-black/30 p-6'
          : 'rounded-xl border border-gray-200 bg-white p-6 shadow-sm'
      }
    >
      <h3 className={`text-lg font-bold ${dark ? 'text-white' : 'text-ink'}`}>
        {title}
      </h3>
      <p
        className={`mt-1 text-sm ${dark ? 'text-white/50' : 'text-ink-muted'}`}
      >
        {summary.description}
      </p>

      <div
        className={`mt-4 space-y-2 border-t pt-4 text-sm ${dark ? 'border-white/10' : 'border-gray-100'}`}
      >
        <div
          className={`flex justify-between ${dark ? 'text-white/50' : 'text-ink-muted'}`}
        >
          <span>Amount</span>
          <span>{money(summary.totalExcl ?? 0, summary.currency ?? '')}</span>
        </div>
        {(summary.taxAmount ?? 0) > 0 && (
          <div
            className={`flex justify-between ${dark ? 'text-white/50' : 'text-ink-muted'}`}
          >
            <span>Tax ({summary.taxPercentage}%)</span>
            <span>{money(summary.taxAmount ?? 0, summary.currency ?? '')}</span>
          </div>
        )}
        <div
          className={`flex justify-between border-t pt-2 text-base font-bold ${
            dark ? 'border-white/10 text-white' : 'border-gray-100 text-ink'
          }`}
        >
          <span>Total Due</span>
          <span>{money(summary.totalIncl ?? 0, summary.currency ?? '')}</span>
        </div>
      </div>

      <p
        className={
          dark
            ? 'mt-4 rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-xs text-primary'
            : 'mt-4 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800'
        }
      >
        You will be redirected to Stripe to complete this payment securely.
      </p>

      {error && (
        <p
          role="alert"
          className={
            dark
              ? 'mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300'
              : 'mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700'
          }
        >
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={handlePayNow}
        disabled={isRedirecting}
        className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
      >
        <CreditCard className="h-4 w-4" />
        {isRedirecting ? 'Redirecting to Stripe...' : 'Pay with Stripe'}
      </button>
    </div>
  );
};

export default PaymentSummary;
